import {
  useDeleteMessage,
  useDoctolibReleve,
  useMessages,
  useSetMessageEnabled,
  useUpdateMessage,
  type MessageCatalogEntry,
  type MessageTemplate,
} from '@fidalia/api';
import { Alert, Button, Card, cn, IconBadge, Spinner } from '@fidalia/ui';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  MdArrowBack,
  MdDelete,
  MdLink,
  MdPause,
  MdPlayArrow,
  MdSave,
  MdSend,
  MdStickyNote2,
} from 'react-icons/md';

import { ApercuMessage } from '@/features/messages/apercu';
import { CarteSignature, Composeur } from '@/features/messages/composeur';
import { CiblesEnvoi, Encadre, QuandEnvoyer } from '@/features/messages/conditions';
import { ModalDesactiver } from '@/features/messages/desactiver-modal';
import { ModalTest } from '@/features/messages/test-modal';
import { casesOf, draftOf, GOOGLE_REVIEW, type MessageDraft } from '@/features/messages/types';
import { isHttpsLink, resolveReviewLink } from '@/features/reviews/google-link';
import { GoogleLinkField } from '@/features/reviews/google-link-field';
import { useGoogleReviewLink } from '@/features/reviews/use-google-review-link';
import { useApercuCabinet } from '@/features/messages/use-apercu';

export const Route = createFileRoute('/_app/messages/$id')({
  component: EditionPage,
});

function EditionPage() {
  const { id } = Route.useParams();
  const { data, isPending } = useMessages();

  if (isPending) {
    return (
      <div className="flex justify-center py-16 text-content-subtle">
        <Spinner className="size-6" />
      </div>
    );
  }

  const template = data?.templates.find((candidate) => candidate.id === id) ?? null;
  const entry = data?.catalog.find((candidate) => candidate.key === template?.catalogKey) ?? null;

  if (!template || !entry) return <Introuvable />;

  // Remonté par la clé : passer d'un modèle à l'autre doit repartir d'un
  // brouillon neuf, jamais continuer celui du précédent. La date de mise à jour
  // en fait partie — un enregistrement réussi rend le brouillon à l'état
  // enregistré.
  return (
    <Edition
      key={`${template.id}:${template.updatedAt}`}
      enregistre={template}
      entry={entry}
      templates={data?.templates ?? []}
    />
  );
}

/**
 * Deux états comparés à l'octet près, listes triées.
 *
 * Le tri n'est pas une coquetterie : décocher un agenda puis le recocher le
 * renvoie en fin de liste, et sans tri l'écran annoncerait des modifications
 * là où rien n'a changé.
 */
function empreinte(draft: MessageDraft): string {
  return JSON.stringify({
    ...draft,
    agendaIds: [...draft.agendaIds].sort((a, b) => a - b),
    motifIds: [...draft.motifIds].sort((a, b) => a - b),
  });
}

/**
 * Édition d'un modèle : les réglages à gauche, le rendu à droite.
 *
 * Page à part entière plutôt que boîte de dialogue — on y reste longtemps, on
 * y relit, on fait des allers-retours entre la phrase et le moment où elle
 * part.
 *
 * Les frappes vont dans un brouillon local, pas dans le modèle : ce qui est
 * enregistré est ce qui partira aux patients, et une phrase laissée à
 * mi-course ne doit pas pouvoir être celle-là. D'où la barre d'enregistrement,
 * qui n'apparaît qu'une fois quelque chose modifié.
 */
function Edition({
  enregistre,
  entry,
  templates,
}: {
  enregistre: MessageTemplate;
  entry: MessageCatalogEntry;
  templates: readonly MessageTemplate[];
}) {
  const { data: releve } = useDoctolibReleve();
  const { expediteur, cabinet, logoUrl, signature } = useApercuCabinet();
  const modifier = useUpdateMessage();
  const basculer = useSetMessageEnabled();

  const [test, setTest] = useState(false);
  const [confirmerDesactivation, setConfirmerDesactivation] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);
  const [draft, setDraft] = useState<MessageDraft>(() => draftOf(enregistre));

  // Le lien Google est unique pour le cabinet : le même champ que celui du formulaire
  // d'avis. Il ne vit pas dans le texte, il part en bouton sous le message.
  const estAvis = entry.key === GOOGLE_REVIEW;
  const lienCabinet = useGoogleReviewLink();
  const [lienSaisi, setLienSaisi] = useState<string | null>(null);
  const lien = lienSaisi ?? lienCabinet.saved;
  const lienAEnregistrer =
    estAvis && lienSaisi !== null && resolveReviewLink(lien) !== lienCabinet.saved;

  /**
   * Ce que la catégorie laisse régler, et qui décide de la charge envoyée.
   *
   * Le serveur refuse ce qui ne se règle pas — le nom d'une catégorie à
   * message unique, le ciblage d'une demande d'avis, le moment d'envoi d'un
   * message verrouillé. Envoyer ces champs « au cas où » aurait fait échouer
   * un enregistrement par ailleurs légitime.
   */
  const nomFige = entry.locked || entry.singleCase;
  const accepteDesCas = !entry.locked && !entry.singleCase;
  const peutCibler = !entry.locked && !(enregistre.isFallback && accepteDesCas);

  // Les noms des motifs visés, pour la confirmation de désactivation. `null`
  // quand le message part pour tous les rendez-vous — `motifIds` vide le dit.
  const motifsVises = useMemo(() => {
    if (draft.motifIds.length === 0) return null;

    const table = new Map(
      (releve?.releve?.motifs ?? []).map((motif) => [
        motif.id,
        { nom: motif.nom, couleur: motif.couleur },
      ]),
    );
    return draft.motifIds
      .map((id) => table.get(id))
      .filter((candidat): candidat is { nom: string; couleur: string | null } => Boolean(candidat));
  }, [draft.motifIds, releve]);

  const modifie = empreinte(draft) !== empreinte(draftOf(enregistre)) || lienAEnregistrer;

  const changer = (champs: Partial<MessageDraft>) =>
    setDraft((courant) => ({ ...courant, ...champs }));

  const signaler = (error: unknown) =>
    setRefus(error instanceof Error ? error.message : 'Opération impossible.');

  const enregistrer = () => {
    setRefus(null);

    if (lienAEnregistrer) {
      if (!isHttpsLink(lien)) return setRefus('Le lien Google doit commencer par https://.');
      lienCabinet.save(resolveReviewLink(lien)).then(() => setLienSaisi(null), signaler);
    }

    modifier.mutate(
      {
        id: enregistre.id,
        subject: draft.subject,
        body: draft.body,
        ...(nomFige ? {} : { name: draft.name }),
        ...(entry.locked
          ? {}
          : { timing: draft.timing, offsetHours: draft.offsetHours, sendHour: draft.sendHour }),
        ...(peutCibler ? { agendaIds: draft.agendaIds, motifIds: draft.motifIds } : {}),
      },
      { onError: signaler },
    );
  };

  const annuler = () => {
    setDraft(draftOf(enregistre));
    setLienSaisi(null);
  };

  /**
   * Activer, c'est laisser partir ce qu'on a sous les yeux : les retouches en
   * attente sont enregistrées dans le même geste. Activer un message tout en
   * laissant partir la version d'avant serait le pire des deux mondes.
   *
   * Le serveur peut refuser l'allumage — une demande d'avis sans lien, un
   * rappel sans créneau, un cas sans motif : son refus s'affiche tel quel,
   * c'est lui qui dit ce qui manque.
   */
  const basculerActif = async () => {
    setRefus(null);
    try {
      if (modifie) enregistrer();
      await basculer.mutateAsync({ id: enregistre.id, enabled: !enregistre.enabled });
    } catch (error) {
      signaler(error);
    }
  };

  // ⌘S / Ctrl+S : le raccourci que cherche la main dès qu'un bouton
  // « Enregistrer » est à l'écran. Sans cela, le navigateur propose
  // d'enregistrer la page, ce qui ne sert à personne ici.
  useEffect(() => {
    const surTouche = (evenement: KeyboardEvent) => {
      if (evenement.key !== 's' || !(evenement.metaKey || evenement.ctrlKey)) return;
      evenement.preventDefault();
      if (modifie) enregistrer();
    };

    globalThis.addEventListener('keydown', surTouche);
    return () => globalThis.removeEventListener('keydown', surTouche);
  });

  // Fermer l'onglet sur un brouillon non enregistré perdrait le texte sans
  // rien dire. La navigation interne, elle, garde le brouillon en mémoire.
  useEffect(() => {
    if (!modifie) return;

    const surFermeture = (evenement: BeforeUnloadEvent) => evenement.preventDefault();
    globalThis.addEventListener('beforeunload', surFermeture);
    return () => globalThis.removeEventListener('beforeunload', surFermeture);
  }, [modifie]);

  return (
    // `pb-24` : la barre d'enregistrement flotte au-dessus du bas de page, et
    // sans cette réserve elle recouvrait le dernier champ une fois descendu
    // tout en bas — exactement au moment où l'on veut le relire avant
    // d'enregistrer.
    <div className="flex flex-col gap-6 pb-24">
      {/* Filet sous l'en-tête : il porte le nom et les actions du message
          entier, le reste de la page n'en règle que des morceaux. */}
      <div className="flex flex-col gap-2 border-b border-border-subtle pb-4">
        <Link
          to="/messages"
          className="inline-flex w-fit items-center gap-1.5 text-sm text-content-muted transition-colors hover:text-content"
        >
          <MdArrowBack aria-hidden="true" className="size-4" />
          Tous les messages
        </Link>

        {/*
         * En-tête de la page : son nom, son état, ses actions rares.
         *
         * Le nom s'édite sur place — c'est le titre, pas une ligne de
         * formulaire. Sauf sur une catégorie qui ne porte qu'un texte :
         * « Demande d'avis Google », « Rappel de rendez-vous » ne sont pas des
         * noms que le cabinet a choisis, c'est ce que le message est.
         */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          {nomFige ? (
            <h1 className="min-w-0 flex-1 truncate py-1 text-xl font-semibold text-content">
              {draft.name}
            </h1>
          ) : (
            <input
              value={draft.name}
              onChange={(evenement) => changer({ name: evenement.target.value })}
              aria-label="Nom du message"
              placeholder="Nom du message"
              className={cn(
                '-mx-2 min-w-0 flex-1 rounded-field border border-transparent bg-transparent px-2 py-1',
                'text-xl font-semibold text-content placeholder:font-normal placeholder:text-content-subtle',
                'transition-colors hover:bg-surface-muted',
                'focus:border-border-input/50 focus:bg-surface focus:outline-none focus:ring-2 focus:ring-brand-600/15',
              )}
            />
          )}

          <div className="flex shrink-0 items-center gap-2">
            {/* Tester avant d'activer : c'est le geste qui précède, pas une
                action de configuration — d'où sa place dans l'en-tête, à
                gauche de l'interrupteur. */}
            <Button
              variant="ghost"
              size="sm"
              icon={<MdSend className="size-4" />}
              onClick={() => setTest(true)}
            >
              Tester
            </Button>

            {/* Un bouton, pas un interrupteur : activer un message est une
                décision qu'on prend une fois, pas une préférence qu'on règle.
                Le libellé dit ce qu'il va faire, donc l'état courant est son
                contraire. */}
            <Button
              size="sm"
              variant={enregistre.enabled ? 'outline' : 'primary'}
              loading={basculer.isPending}
              // Lecture et pause : le vocabulaire des automatisations, où l'on
              // met un envoi en service ou on le suspend.
              icon={
                enregistre.enabled ? (
                  <MdPause className="size-4" />
                ) : (
                  <MdPlayArrow className="size-4" />
                )
              }
              onClick={() => {
                if (!enregistre.enabled) return void basculerActif();
                setConfirmerDesactivation(true);
              }}
            >
              {enregistre.enabled ? 'Désactiver' : 'Activer'}
            </Button>

            {/* Ni dupliquer ni supprimer une catégorie : la liste ne montre
                qu'un catalogue fermé. Un cas, en revanche, s'efface — il est né
                d'un choix, et c'est ici qu'on l'a sous les yeux. */}
            {accepteDesCas && !enregistre.isFallback ? (
              <SupprimerCas courant={enregistre} templates={templates} onErreur={signaler} />
            ) : null}
          </div>
        </div>
      </div>

      {/* Le refus du serveur : droits insuffisants, mise en route incomplète,
          motif déjà visé par un autre cas. Repris tel quel — il dit ce qui
          manque mieux qu'un message générique. */}
      {refus ? <Alert tone="danger">{refus}</Alert> : null}

      <ModalTest
        open={test}
        onClose={() => setTest(false)}
        templateId={enregistre.id}
        nomModele={draft.name}
      />

      <ModalDesactiver
        open={confirmerDesactivation}
        nom={draft.name}
        motifs={motifsVises}
        onAnnuler={() => setConfirmerDesactivation(false)}
        onConfirmer={() => {
          void basculerActif();
          setConfirmerDesactivation(false);
        }}
      />

      {/*
       * À gauche ce qui se règle, à droite ce que ça donne. La colonne de
       * gauche suit l'ordre des questions : à qui ça s'adresse, ce qu'on leur
       * écrit, ce qu'on signe, et quand ça part. La cible ouvre la colonne
       * parce qu'elle commande le texte — on n'écrit pas la même consigne pour
       * une extraction et pour un détartrage.
       */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,26rem)] lg:items-start">
        <div className="flex flex-col gap-6">
          <CiblesEnvoi
            draft={draft}
            entry={entry}
            isFallback={enregistre.isFallback}
            onChange={changer}
          />

          {estAvis ? (
            <div className="flex flex-col gap-2">
              <label
                htmlFor="lien-google"
                className="inline-flex items-center gap-2 text-sm font-medium text-content"
              >
                <span aria-hidden="true" className="text-content-subtle [&>svg]:size-4">
                  <MdLink />
                </span>
                Lien Google
              </label>
              <Encadre>
                <div className="flex flex-col gap-3">
                  <p className="text-sm leading-relaxed text-content-muted">
                    Le même lien pour tout le cabinet. Il s’affiche en bouton à la fin du message.
                  </p>
                  <GoogleLinkField id="lien-google" value={lien} onChange={setLienSaisi} />
                </div>
              </Encadre>
            </div>
          ) : null}

          <Composeur draft={draft} onChange={changer} />

          <CarteSignature />

          <QuandEnvoyer draft={draft} entry={entry} onChange={changer} />
        </div>

        <div className="lg:sticky lg:top-6">
          <ApercuMessage
            expediteur={expediteur}
            cabinet={cabinet}
            logoUrl={logoUrl}
            objet={draft.subject}
            corps={draft.body}
            signature={signature.body}
            logoSignature={signature.withLogo}
            bouton={
              estAvis ? { label: 'Donner mon avis', url: resolveReviewLink(lien) } : undefined
            }
          />
        </div>
      </div>

      <BarreEnregistrement
        visible={modifie}
        enCours={modifier.isPending}
        onAnnuler={annuler}
        onEnregistrer={enregistrer}
      />
    </div>
  );
}

/**
 * Barre d'enregistrement, flottante au bas de l'écran.
 *
 * Flottante et non en pied de page : la page est longue, et un bouton en bas
 * de colonne n'est sous la main qu'une fois qu'on a fini de descendre. Ici il
 * suit le regard, où qu'on en soit dans la relecture.
 *
 * Toujours montée, seulement translatée hors champ : elle glisse au premier
 * caractère tapé au lieu d'apparaître d'un coup, et rien ne saute quand elle
 * repart. `pointer-events-none` au repos, sinon elle intercepterait les clics
 * au bas de la page alors qu'elle est invisible.
 */
function BarreEnregistrement({
  visible,
  enCours,
  onAnnuler,
  onEnregistrer,
}: {
  visible: boolean;
  enCours: boolean;
  onAnnuler: () => void;
  onEnregistrer: () => void;
}) {
  return (
    <div
      aria-hidden={!visible}
      className={cn(
        'pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4',
        'transition-[opacity,transform] duration-200',
        visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
      )}
    >
      <div
        className={cn(
          'flex items-center gap-3 rounded-card border border-border-subtle bg-surface py-2 pl-5 pr-2',
          'shadow-[0_18px_40px_-16px_rgba(6,34,54,0.45)]',
          visible && 'pointer-events-auto',
        )}
      >
        <p className="text-sm text-content-muted">Modifications non enregistrées</p>

        <Button variant="ghost" size="sm" onClick={onAnnuler} tabIndex={visible ? undefined : -1}>
          Annuler
        </Button>
        <Button
          size="sm"
          loading={enCours}
          icon={<MdSave className="size-4" />}
          onClick={onEnregistrer}
          tabIndex={visible ? undefined : -1}
        >
          Enregistrer
        </Button>
      </div>
    </div>
  );
}

/**
 * Effacer le cas qu'on a sous les yeux.
 *
 * Jamais le filet de sécurité d'une catégorie : lui parti, les rendez-vous
 * qu'aucun cas ne vise n'auraient plus rien. Le serveur le refuse également.
 */
function SupprimerCas({
  courant,
  templates,
  onErreur,
}: {
  courant: MessageTemplate;
  templates: readonly MessageTemplate[];
  onErreur: (error: unknown) => void;
}) {
  const supprimer = useDeleteMessage();
  const navigate = useNavigate();

  /**
   * Après suppression, on repart sur le premier cas restant : rester sur
   * l'URL d'un message qui n'existe plus afficherait « Ce modèle n'existe
   * pas » à quelqu'un qui vient d'appuyer sur « Supprimer ».
   */
  const supprimerCourant = () => {
    const suivant = casesOf(templates, courant.catalogKey).find(
      (candidat) => candidat.id !== courant.id,
    );

    supprimer.mutate(courant.id, {
      onSuccess: () => {
        if (suivant) void navigate({ to: '/messages/$id', params: { id: suivant.id } });
        else void navigate({ to: '/messages' });
      },
      onError: onErreur,
    });
  };

  return (
    <button
      type="button"
      onClick={supprimerCourant}
      disabled={supprimer.isPending}
      aria-label="Supprimer ce cas"
      title="Supprimer ce cas"
      className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-content-subtle transition-colors hover:bg-danger-soft hover:text-danger disabled:opacity-50"
    >
      <MdDelete aria-hidden="true" className="size-4" />
    </button>
  );
}

/** Cas supprimé, ou identifiant inventé dans l'URL. */
function Introuvable() {
  return (
    <Card className="flex flex-col items-center gap-5 px-6 py-14 text-center">
      <IconBadge size="lg">
        <MdStickyNote2 />
      </IconBadge>
      <div className="flex max-w-sm flex-col gap-2">
        <p className="text-xl font-semibold text-content">Ce modèle n’existe pas</p>
        <p className="text-sm leading-relaxed text-balance text-content-muted">
          Il a peut-être été supprimé depuis un autre poste du cabinet.
        </p>
      </div>
      <Link to="/messages" className="text-sm font-medium text-brand-600 hover:underline">
        Revenir aux messages
      </Link>
    </Card>
  );
}
